// @todo: Migrate to 'Divider' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Divider} from '@material-ui/core';
import {IconButton} from '@wandb/components';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import {omit} from 'lodash';
import React, {useContext, useState} from 'react';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from 'react-toastify';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Dropdown} from 'semantic-ui-react';

import * as Analytics from '../../../services/analytics';
import {PanelBankUpdaterContext} from '../../../state/panelbank/context';
import {useReportCommentAlerts} from '../../../state/reports/hooks';
import {isInIframe} from '../../../util/window';
import {useLoadedReportContext} from '../LoadedReportContext';
import {useReportPageContext} from '../ReportPageContext';
import {ReportMode} from '../types';
import * as S from './ReportPageHeader.styles';
import {ReportPageMenuItem} from './ReportPageMenuItem';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {ReportPageModal} from './ReportPageModal';

type ReportPageMenuActionsProps = {
  viewerOwnsReport: boolean;
};

export enum ReportPageAction {
  EditReport,
  EditUpdatedReport,
  DraftReportWarning,

  SetPreviewImage,
  CloneDraft,
  DownloadPDF,
  ToggleNotifications,
  ViewStars,

  MoveReport,
  DeleteDraft,
}

export const ReportPageMenuActions = ({
  viewerOwnsReport,
}: ReportPageMenuActionsProps) => {
  const {
    isAdmin,
    projectName,
    reportMode,
    viewedReportId,
    viewer,
    isWBFeaturedReport,
    project,
    teamMemberInfo: {viewerIsTeamMember},
    isPublicProject,
  } = useReportPageContext();
  const {reportViewRef, view} = useLoadedReportContext();

  const isInEditMode = reportMode === ReportMode.IS_EDITING;
  const isInViewMode = reportMode === ReportMode.IS_VIEWING;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [modalType, setModalType] = useState<ReportPageAction | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const {isSubscribed, subscribe, unsubscribe} = useReportCommentAlerts(
    reportViewRef,
    view
  );
  const {setDisableWaitToLoad} = useContext(PanelBankUpdaterContext);

  // Determine if viewer has ability to edit report
  const hasReportEditAccess =
    viewer != null &&
    !isInIframe() &&
    !project.readOnly &&
    (!view.locked || viewerOwnsReport || isAdmin);
  const hasGalleryEditAccess =
    viewer != null &&
    (viewer.id === view.user.id || isAdmin || viewerOwnsReport);
  const hasEditAccess =
    projectName == null || isWBFeaturedReport
      ? hasGalleryEditAccess
      : hasReportEditAccess;

  // A viewer with an account has access to copy report and turn on on notifications
  const viewerIsLoggedIn = viewer != null && !isInIframe();

  const primaryOptions = [
    {
      icon: <Icon name="pencil-edit" style={{marginRight: '8px'}} />,
      text: isLoading ? 'Loading...' : 'Edit report',
      onClick: () => {
        setIsLoading(true);
        setModalType(ReportPageAction.EditUpdatedReport);
      },
      isEnabled: isInViewMode && hasEditAccess,
    },
    {
      icon: <Icon name="photo" />,
      text: 'Set preview image',
      onClick: () => {
        setModalType(ReportPageAction.SetPreviewImage);
      },
      isEnabled: isInEditMode,
    },
    {
      icon: <Icon name="copy" />,
      text: 'Make a copy',
      onClick: () => {
        setModalType(ReportPageAction.CloneDraft);
      },
      isEnabled: viewerIsLoggedIn,
    },
    {
      icon: <Icon name="download" />,
      text: 'Download',
      onClick: () => {
        setModalType(ReportPageAction.DownloadPDF);
        setDisableWaitToLoad(true);
      },
      isEnabled: true,
    },
    {
      icon: <Icon name="bell-notifications" />,
      text: 'Notifications',
      toggle: <S.ReportNotificationsToggle toggle checked={isSubscribed} />,
      onClick: () => {
        Analytics.reportNotificationsToggled({
          isPublicProject,
          reportId: viewedReportId,
          actionTriggered: isSubscribed ? 'unsubscribe' : 'subscribe',
        });

        if (isSubscribed) {
          unsubscribe();
          toast('Notifications successfully turned off.');
        } else {
          subscribe();
          toast('Notifications successfully turned on.');
        }
      },
      isEnabled: (isWBFeaturedReport || isInViewMode) && viewerIsLoggedIn,
    },
    {
      icon: <Icon name="star" />,
      text: 'View stars',
      onClick: () => {
        Analytics.reportViewLikesClicked({isPublicProject});
        setModalType(ReportPageAction.ViewStars);
      },
      isEnabled:
        isInViewMode &&
        (isAdmin || (!isWBFeaturedReport && viewerIsTeamMember)),
    },
  ].filter(opt => opt.isEnabled);

  const secondaryOptions = [
    {
      icon: <Icon name="folder-project-move" />,
      text: 'Move report',
      onClick: () => {
        setModalType(ReportPageAction.MoveReport);
      },
      isEnabled: isInViewMode && isAdmin, // This is a W&B admin or server admin specific ability
    },
    {
      icon: <Icon name="delete" />,
      text: 'Delete draft',
      onClick: () => {
        setModalType(ReportPageAction.DeleteDraft);
      },
      isEnabled: !isWBFeaturedReport && isInEditMode,
    },
  ].filter(opt => opt.isEnabled);

  if (primaryOptions.length + secondaryOptions.length === 1) {
    return (
      <>
        <IconButton
          data-test="single-report-overflow-action-button"
          aria-label="More actions"
          icon="pencil-edit"
          variant={isInEditMode ? 'secondary' : 'ghost'}
          onPress={primaryOptions[0].onClick}
        />
        <ReportPageModal
          modalType={modalType}
          setModalType={setModalType}
          setIsLoading={setIsLoading}
        />
      </>
    );
  }

  return (
    <div>
      <IconButton
        aria-label="More actions"
        data-test="report-overflow-menu-button"
        icon="overflow-horizontal"
        tooltip={{children: 'More actions'}}
        variant={isInEditMode ? 'secondary' : 'ghost'}
        onPress={() => setIsMenuOpen(!isMenuOpen)}
      />
      <Dropdown
        data-test="report-overflow-dropdown-menu"
        open={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        floating
        icon={<></>}
        direction="left">
        <S.ReportActionsMenu style={{padding: 0}}>
          {primaryOptions.map((optionProps, index) => (
            <ReportPageMenuItem
              applyMarginTop={index === 0}
              applyMarginBottom={index === primaryOptions.length - 1}
              key={'primary-option-' + index}
              {...omit(optionProps, 'isEnabled')}
            />
          ))}
          {secondaryOptions.length > 0 && <Divider />}
          {secondaryOptions.map((optionProps, index) => (
            <ReportPageMenuItem
              applyMarginTop={index === 0}
              applyMarginBottom={index === secondaryOptions.length - 1}
              destructive
              key={'secondary-option-' + index}
              {...omit(optionProps, 'isEnabled')}
            />
          ))}
        </S.ReportActionsMenu>
      </Dropdown>
      <ReportPageModal
        modalType={modalType}
        setModalType={setModalType}
        setIsLoading={setIsLoading}
      />
    </div>
  );
};
